import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../Icon';
import { ICONS } from '../icon-map';
import { Skeleton } from '../EmptyState';
import '../ds.css';

/** Tiny inline sparkline (no library). values: number[] */
export function Sparkline({ values = [], width = 80, height = 26, color = 'var(--accent)', fill = false }) {
    const nums = values.map(Number).filter((n) => Number.isFinite(n));
    if (nums.length < 2) return null;
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    const span = max - min || 1;
    const step = width / (nums.length - 1);
    const pts = nums.map((v, i) => `${(i * step).toFixed(1)},${(height - 2 - ((v - min) / span) * (height - 4)).toFixed(1)}`);
    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
            {fill && <path d={`M0,${height} L${pts.join(' L')} L${width},${height} Z`} fill={color} fillOpacity="0.12" />}
            <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    );
}

/**
 * Stat: one KPI tile.
 * label, value, icon, delta ({ value: '9%', direction: 'up'|'down'|'flat', good: true }),
 * meta (secondary line), tone for the value ('danger' | 'warning' | 'success'),
 * trend (number[] for a sparkline), aside (custom right-side node), to (link) / onClick.
 */
export default function Stat({ label, value, icon, delta, meta, tone, trend, aside, to, onClick, loading, className = '', title }) {
    const body = (
        <>
            <span className="ds-stat__label">
                {icon && <Icon glyph={icon} size="sm" />}
                {label}
            </span>
            <span className="ds-stat__row">
                {loading ? (
                    <Skeleton width={72} height={24} />
                ) : (
                    <span className={`ds-stat__value ${tone ? `ds-stat__value--${tone}` : ''}`}>{value ?? '—'}</span>
                )}
                {(aside || (trend && trend.length > 1)) && (
                    <span className="ds-stat__aside">{aside || <Sparkline values={trend} />}</span>
                )}
            </span>
            {(delta || meta) && (
                <span className="ds-stat__meta">
                    {delta && (
                        <span className={`ds-delta ds-delta--${delta.good === false ? 'down' : delta.good === true ? 'up' : (delta.direction || 'flat')}`}>
                            {delta.direction === 'up' && <Icon glyph={ICONS.chevronUp} size="xs" />}
                            {delta.direction === 'down' && <Icon glyph={ICONS.chevronDown} size="xs" />}
                            {delta.value}
                        </span>
                    )}
                    {meta && <span>{meta}</span>}
                </span>
            )}
        </>
    );

    const classes = `ds-stat ${className}`;
    if (to) return <Link to={to} className={classes} title={title}>{body}</Link>;
    if (onClick) return <button type="button" onClick={onClick} className={classes} title={title}>{body}</button>;
    return <div className={classes} title={title}>{body}</div>;
}

export function StatGrid({ children, className = '', min = 200 }) {
    return (
        <div className={`ds-stat-grid ${className}`} style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))` }}>
            {children}
        </div>
    );
}
